"use client";

import React, { useEffect, useRef, useState } from "react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.15,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function TheHumanBehindTheWork() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>();
  const introReveal = useRevealOnScroll<HTMLDivElement>();
  const imageOneReveal = useRevealOnScroll<HTMLDivElement>();
  const middleTextReveal = useRevealOnScroll<HTMLDivElement>();
  const imageTwoReveal = useRevealOnScroll<HTMLDivElement>();
  const conclusionReveal = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="87111" id="intersection-of-passion-and-strategy" className="dark bg-background text-foreground py-24 sm:py-32 overflow-hidden">
      {}
      <div ref={headerReveal.ref} className={`mx-auto max-w-2xl px-6 text-center transition-all duration-1000 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <h2 className="font-default text-5xl tracking-tighter text-foreground sm:text-6xl mb-6 uppercase font-bold">Intersection of Enthusiasts &amp; Community</h2>
        <Separator className="mx-auto w-16 bg-primary" />
      </div>

      {}
      <div ref={introReveal.ref} className={`mx-auto max-w-2xl px-6 mt-16 text-center transition-all duration-1000 delay-150 ease-out ${introReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <p className="text-lg leading-relaxed text-secondary font-default mb-8">Having been heavily involved within the community, on a social and content basis, automotive culture has never been stronger. My interest has always been driven by constant innovation and the initiative to engage with the machine and those who share that perspective. Creating an outlet to engage and share my take on the ever-evolving industry is a project I'm excited to explore.</p>
        <p className="text-lg leading-relaxed text-secondary font-default">Even during times of uncertainty, and an unclear path forward, the enthusiast market prevails and is still tailored to on a daily basis. We just need to be a steady reminder, so we can continue to see and experience the cars we fawned over when we were younger!</p>
      </div>

      {}
      <div ref={imageOneReveal.ref} className={`mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 mt-24 transition-all duration-1000 ease-out ${imageOneReveal.isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
        <div className="overflow-hidden rounded-sm shadow-xl shadow-primary/10 border border-white/5">
          <AspectRatio ratio={21 / 9}>
            <img src="https://autopulseca.com/wp-content/uploads/2026/04/20250712_165634-scaled.jpg" alt="Solomon Brown, blending professional marketing expertise with automotive passion" className="object-cover w-full h-full grayscale hover:grayscale-0 transition-all duration-700" />
            <div className="absolute inset-0 from-background/40 to-transparent mix-blend-multiply !opacity-[0] !bg-[hsl(0,79%,51%)] !text-[hsl(0,91%,45%)]" />
          </AspectRatio>
        </div>
      </div>

      {}
      <div ref={middleTextReveal.ref} className={`mx-auto max-w-2xl px-6 mt-24 text-center transition-all duration-1000 ease-out ${middleTextReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <h3 className="font-default text-3xl text-foreground mb-6 italic font-semibold tracking-tight">"Whether you are driven to wrench, or wrenching to drive, you and your car fueled by enthusiasm"</h3>
        <p className="text-lg leading-relaxed text-secondary font-default mb-8">Enjoying your car on a windy road, at a track, or working on it are key indicators of being an automotive enthusiast. There are experiences that last a lifetime. Similar to waiting for the next model unveiling from a beloved brand, we wait eagerly to see what will come to market and look forward to discussing and possibly enjoying it in the future!</p>
        
      </div>

      {}
      <div ref={imageTwoReveal.ref} className={`mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 mt-24 transition-all duration-1000 ease-out ${imageTwoReveal.isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
        <div className="overflow-hidden rounded-sm shadow-xl shadow-primary/10 border border-white/5">
          <AspectRatio ratio={21 / 9}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1631141/img/embodiment_tactile_nature_wide.png" alt="Detail shot representing the tactile precision of automotive engineering and strategic planning" className="object-cover w-full h-full grayscale hover:grayscale-0 transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent mix-blend-multiply" />
          </AspectRatio>
        </div>
      </div>

      {}
      <div ref={conclusionReveal.ref} className={`mx-auto max-w-2xl px-6 mt-24 text-center transition-all duration-1000 ease-out ${conclusionReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <p className="text-lg leading-relaxed text-secondary font-default mb-12">
          This work is high-stakes. It requires a willingness to push the boundaries of traditional marketing and lean into the curves of innovation. My role is to lead with calculated precision, offering a high-octane approach to communications, brand strategy, and professional growth.
        </p>
        
        <div className="flex flex-col items-center justify-center gap-6">
          <Separator className="w-24 bg-primary/30" />
          <Button asChild variant="outline" size="lg" className="rounded-none px-10 font-default tracking-widest text-sm h-14 border-primary/40 hover:bg-primary/10 hover:border-primary transition-all duration-300 uppercase">
            <Link to="#">
              Ignite the Conversation
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}